<template>
  <div class="bet-num-list">
    <!-- 彩票类型切换标签：默认双色球 -->
    <div class="type-tabs">
      <div
        v-for="t in typeList"
        :key="t.value"
        class="type-tab"
        :class="{ active: lotteryType === t.value }"
        @click="switchType(t.value)"
      >
        {{ t.label }}
      </div>
    </div>

    <!-- 投注号码列表 -->
    <div class="bet-list">
      <div v-for="row in rows" :key="row.betNumIssue + row.betTime" class="bet-item">
        <!-- 期号 + 投注时间 -->
        <div class="bet-head">
          <span class="bet-expect">第 {{ row.betNumIssue }} 期</span>
          <span class="bet-time">{{ row.betTime }}</span>
        </div>

        <!-- 标签行：投注方式（单式/复式） + 玩法（如 7+1） -->
        <div class="bet-tags">
          <span class="tag tag-blue">{{ betTypeText(row.betType) }}</span>
          <span v-if="row.playType" class="tag tag-orange">
            {{ row.playType }}
          </span>
        </div>

        <!-- 号码球：betNumDTO.redNum 主号码红色，blueNum 辅助号码（如双色球蓝球）蓝色 -->
        <div class="bet-nums">
          <span v-for="(n, i) in mainNums(row.betNumDTO)" :key="'m' + i" class="num-ball">{{ n }}</span>
          <template v-if="extraNums(row.betNumDTO).length">
            <span class="num-plus">+</span>
            <span v-for="(n, i) in extraNums(row.betNumDTO)" :key="'e' + i" class="num-ball blue">{{ n }}</span>
          </template>
        </div>

        <!-- 中奖情况：按后端 numStatus 字段判断
             pending=待兑奖（橙色，附开奖时间 drawTime），win=中奖（绿色），not_win=未中奖（灰色） -->
        <div v-if="row.numStatus === 'pending'" class="prize-detail waiting">
          待兑奖
          <span v-if="row.drawTime" class="draw-time">（开奖时间:{{ row.drawTime }}）</span>
        </div>
        <div v-else class="prize-detail" :class="{ lose: row.numStatus === 'not_win' }">
          {{ row.prizeDetail }}
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="!loading && rows.length === 0" class="empty">暂无投注记录</div>

      <!-- 加载更多：点一次多取一页，取完提示没有更多 -->
      <div
        v-if="rows.length > 0"
        class="load-more"
        @click="loadMore"
      >
        {{ hasMore ? (loading ? '加载中...' : '加载更多') : '— 没有更多了 —' }}
      </div>
    </div>

    <!-- 请求失败提示 -->
    <div v-if="errorMsg" class="error-msg">{{ errorMsg }}</div>
  </div>
</template>

<script>
import { getBetNumPage } from '@/api/lottery'
// 彩票类型枚举统一从工具文件引入
import { lotteryTypeList } from '@/utils/lottery'

export default {
  name: 'BetNumListView',
  data() {
    return {
      // 当前选中的彩票类型，默认双色球（请求参数名 lotteryType）
      lotteryType: 'ssq',
      // 彩票类型枚举（统一维护在 @/utils/lottery）
      typeList: lotteryTypeList,
      rows: [],     // 当前列表数据（加载更多时往后追加）
      total: 0,     // 后端返回的总条数
      pageNum: 1,   // 当前页码
      pageSize: 10, // 每页条数
      loading: false,
      errorMsg: ''
    }
  },
  computed: {
    // 已加载条数少于总数，说明还能继续加载
    hasMore() {
      return this.rows.length < this.total
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    // 投注方式编码转中文：single=单式，multiple未知值原样显示
    betTypeText(value) {
      const map = { single: '单式', multiple: '复式'}
      return map[value] || value || ''
    },
    // 主号码：取投注号码对象 betNumDTO 里的 redNum，按逗号拆成数组
    mainNums(betNumDTO) {
      return (betNumDTO && betNumDTO.redNum ? betNumDTO.redNum : '')
        .split(',').filter(Boolean)
    },
    // 辅助号码：取 betNumDTO 里的 blueNum（如双色球蓝球），没有则返回空数组
    extraNums(betNumDTO) {
      return (betNumDTO && betNumDTO.blueNum ? betNumDTO.blueNum : '')
        .split(',').filter(Boolean)
    },
    // 请求投注号码列表
    async fetchList() {
      this.loading = true
      this.errorMsg = ''
      try {
        const res = await getBetNumPage({
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          lotteryType: this.lotteryType
        })
        // 后端返回格式：{ code: "200", data: { total, rows }, msg, success }
        if (res.code === '200' || res.success) {
          this.rows = this.rows.concat(res.data.rows || [])
          this.total = res.data.total || 0
        } else {
          this.errorMsg = res.msg || '请求失败，请重试'
        }
      } catch (err) {
        this.errorMsg = '网络错误，请稍后重试'
        console.error(err)
      } finally {
        this.loading = false
      }
    },
    // 切换彩票类型：清空数据后重新从第一页请求
    switchType(value) {
      if (this.lotteryType === value) return
      this.lotteryType = value
      this.rows = []
      this.total = 0
      this.pageNum = 1
      this.fetchList()
    },
    // 加载下一页，追加到列表
    loadMore() {
      if (!this.hasMore || this.loading) return
      this.pageNum += 1
      this.fetchList()
    }
  }
}
</script>

<style scoped>
/* ===== 类型切换标签 ===== */
.type-tabs {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  /* 类型超过 4 个时横向滑动查看，一屏最多显示 4 个 */
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; /* iOS 滑动惯性 */
  scrollbar-width: none; /* Firefox 隐藏滚动条 */
}

/* Chrome/Safari 隐藏滚动条但保留滑动能力 */
.type-tabs::-webkit-scrollbar {
  display: none;
}

.type-tab {
  /* 固定占 1/4 宽度，一屏正好 4 个；超出部分横向滑动，不被挤压 */
  flex: 0 0 25%;
  box-sizing: border-box;
  text-align: center;
  padding: 12px 4px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
}

/* 选中态：主题色文字 + 底部短横线 */
.type-tab.active {
  color: #667eea;
  font-weight: bold;
}

.type-tab.active::after {
  content: '';
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  width: 28px;
  height: 3px;
  border-radius: 2px;
  background: #667eea;
}

/* ===== 投注号码列表 ===== */
.bet-list {
  padding: 12px;
}

.bet-item {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 12px;
}

.bet-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.bet-expect {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
}

.bet-time {
  font-size: 12px;
  color: #909399;
}

/* 标签行 */
.bet-tags {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}

.tag {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 18px;
}

.tag-blue {
  background: #ecf0ff;
  color: #667eea;
}

.tag-orange {
  background: #fdf3e6;
  color: #e6a23c;
}

/* 号码球：允许换行（复式号码较多） */
.bet-nums {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.num-ball {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f56c6c; /* 主号码：红色球 */
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.num-ball.blue {
  background: #409eff; /* 辅助号码：蓝色球 */
}

.num-plus {
  color: #909399;
  font-weight: bold;
}

/* 中奖情况：默认中奖绿色，未中奖灰色 */
.prize-detail {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #f0f9eb;
  color: #67c23a;
  font-size: 12px;
  line-height: 1.5;
}

.prize-detail.lose {
  background: #f4f4f5;
  color: #909399;
}

.prize-detail.waiting {
  background: #fdf6ec;
  color: #e6a23c;
}

/* 待开奖条内的开奖时间：跟在“待开奖”后面，同色稍淡 */
.prize-detail.waiting .draw-time {
  margin-left: 4px;
  opacity: 0.9;
}

/* 空状态 */
.empty {
  text-align: center;
  color: #909399;
  font-size: 13px;
  padding: 40px 0;
}

/* 加载更多 */
.load-more {
  text-align: center;
  color: #667eea;
  font-size: 13px;
  padding: 14px 0;
  cursor: pointer;
}

/* 请求失败提示 */
.error-msg {
  text-align: center;
  color: #f56c6c;
  font-size: 13px;
  padding: 20px 0;
}

/* 手机端适配 */
@media screen and (max-width: 480px) {
  .num-ball {
    width: 28px;
    height: 28px;
    font-size: 11px;
  }
}
</style>
